import { FormattedMessage } from 'react-intl';

// Components
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  SetupLabel,
  SetupSwitch,
  SetupValue,
  SetupDatePicker,
} from '../shared';
import ProgramPassbookRate from '../ProgramPassbookRate';
import SetupGridItem from '../../../SetupGridItem';
import { AnnualRecertMonthPicker } from '../shared';
import OnOffTypography from '../../../OnOffTypography';
import PropertyStateConfig from '../../../PropertyStateConfig';
import CertificationPastDue from '../../components/CertificationPastDue';
import RecertType from '../../components/RecertType';

// Constants
import messages from './messages';
import messagesAffordableSetup from '../../messages';

import type { LIHTCValues, PropertyStateConfigValues } from '../../../types';
import { stripOnlyWhiteSpaceToNull } from '../../utils';
import { useMemo } from 'react';
import moment from 'moment';

interface LIHTCflags {
  initialCertsRequired: boolean;
  hasHUDProgramOnly: boolean;
}

type LIHTCSetupValues = LIHTCValues & {
  flags: LIHTCflags,
};
type Props = {
  promptToaster: Function,
  isLoadingPassbookRateData: boolean,
  editMode: boolean,
  onChange: Function,
  values: LIHTCSetupValues,
} & PropertyStateConfigValues;

const LIHTCSetup = ({
  editMode,
  onChange,
  values,
  promptToaster,
  isLoadingPassbookRateData,
  state,
  stateConfigs,
}: Props) => {
  const {
    stateId,
    projectIdTic,
    XMLPropertyName,
    XMLProjectName,
    LIHTCContractEffectiveDate,
    LIHTCContractExpirationDate,
    requiresInitialCertificationForNonHUD,
    isOverrideQualificationEnabled,
    autoRolloverIncomeAndAssets,
    interimCert,
    annualRecertMonth,
    flags: { initialCertsRequired, hasHUDProgramOnly },
    passbookRates,
    hotmaConfig,
    certificationPastDueDefinition,
    keyNumber,
    recertTypes,
  } = values;

  // Validate contract dates
  const isExpirationBeforeEffective = useMemo(() => {
    if (!LIHTCContractEffectiveDate || !LIHTCContractExpirationDate)
      return false;
    return moment(LIHTCContractExpirationDate).isBefore(
      moment(LIHTCContractEffectiveDate),
    );
  }, [LIHTCContractEffectiveDate, LIHTCContractExpirationDate]);

  const handleLIHTCAnnualRecertMonthChange = (
    date: Object,
    validate: Object,
  ) => {
    onChange({
      target: { name: 'setup.LIHTCAnnualRecertMonth', value: date },
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant={'titleLarge'}>
          <FormattedMessage {...messages.title} />
        </Typography>
      </Grid>

      <SetupGridItem
        label={<FormattedMessage {...messages.stateProjectIdXml} />}
      >
        <SetupValue
          editMode={editMode}
          name="setup.stateId"
          value={stateId || ''}
          placeholder="i.e. AR012-345"
          onChange={onChange}
          required
        />
      </SetupGridItem>
      <SetupGridItem
        label={<FormattedMessage {...messages.stateProjectIdTicHec} />}
      >
        <SetupValue
          editMode={editMode}
          name="setup.projectIdTic"
          value={projectIdTic || ''}
          placeholder="i.e. AR012-345"
          onChange={onChange}
          editable={editMode}
          required
        />
      </SetupGridItem>
      <SetupGridItem
        label={<FormattedMessage {...messages.contractEffectiveDate} />}
      >
        <SetupDatePicker
          editMode={editMode}
          name="setup.LIHTCContractEffectiveDate"
          value={LIHTCContractEffectiveDate}
          onChange={onChange}
          id="lihtc-contract-effective-date"
        />
      </SetupGridItem>
      <SetupGridItem
        label={<FormattedMessage {...messages.contractExpirationDate} />}
      >
        <SetupDatePicker
          editMode={editMode}
          name="setup.LIHTCContractExpirationDate"
          value={LIHTCContractExpirationDate}
          onChange={onChange}
          id="lihtc-contract-expiration-date"
          error={isExpirationBeforeEffective}
          helperText={
            isExpirationBeforeEffective ? (
              <Typography variant="bodyMedium" color="error">
                <FormattedMessage {...messages.expirationBeforeEffectiveDate} />
              </Typography>
            ) : null
          }
        />
      </SetupGridItem>
      <RecertType
        editMode={editMode}
        program="LIHTC"
        recertTypes={recertTypes}
        onChange={onChange}
      />
      <SetupGridItem label={<FormattedMessage {...messages.interimCerts} />}>
        <SetupSwitch
          switchId={'interim-certs-switch'}
          editMode={editMode}
          name="setup.interimCert"
          disabled={!editMode}
          value={interimCert}
          onChange={onChange}
        />
      </SetupGridItem>
      {!hasHUDProgramOnly && initialCertsRequired && (
        <SetupGridItem
          label={<FormattedMessage {...messages.initialCertForNonHUD} />}
          TooltipProps={{
            title: <FormattedMessage {...messages.initialCertForNonHUDTip} />,
          }}
        >
          <SetupSwitch
            switchId={'initial-cert-for-non-HUD-switch'}
            editMode={editMode}
            name="setup.requiresInitialCertificationForNonHUD"
            disabled={!editMode}
            value={requiresInitialCertificationForNonHUD}
            onChange={onChange}
          />
        </SetupGridItem>
      )}
      <SetupGridItem
        label={<FormattedMessage {...messages.overrideQualification} />}
      >
        <SetupSwitch
          switchId={'override-qualification-switch'}
          editMode={editMode}
          name="setup.isOverrideQualificationEnabled"
          disabled={!editMode}
          value={isOverrideQualificationEnabled}
          onChange={onChange}
        />
      </SetupGridItem>
      {[
        <SetupGridItem
          label={<FormattedMessage {...messages.altXMLPropertyName} />}
        >
          <SetupValue
            editMode={editMode}
            name="setup.XMLPropertyName"
            value={XMLPropertyName}
            placeholder=""
            onChange={({ target: { name, value } }) => {
              onChange({
                target: {
                  name,
                  value: stripOnlyWhiteSpaceToNull(value),
                },
              });
            }}
            maxLength={'100'}
          />
        </SetupGridItem>,
        <SetupGridItem
          label={<FormattedMessage {...messages.altXMLProjectName} />}
          TooltipProps={{
            title: <FormattedMessage {...messages.altXMLProjectNameTooltip} />,
          }}
        >
          <SetupValue
            editMode={editMode}
            name="setup.XMLProjectName"
            value={XMLProjectName}
            placeholder=""
            onChange={({ target: { name, value } }) => {
              onChange({
                target: {
                  name,
                  value: stripOnlyWhiteSpaceToNull(value),
                },
              });
            }}
            maxLength={'100'}
          />
        </SetupGridItem>,
      ]}
      {state === 'US-FL' && (
        <SetupGridItem label={<FormattedMessage {...messages.keyNumber} />}>
          <SetupValue
            editMode={editMode}
            name="setup.keyNumber"
            value={keyNumber}
            placeholder=""
            onChange={({ target: { name, value } }) => {
              onChange({
                target: {
                  name,
                  value: stripOnlyWhiteSpaceToNull(value),
                },
              });
            }}
            type={'number'}
            maxLength={'4'}
            minLength={'4'}
          />
        </SetupGridItem>
      )}
      <SetupGridItem
        label={
          <FormattedMessage
            {...messagesAffordableSetup.annualRecertMonth}
            values={{ program: 'LIHTC' }}
          />
        }
      >
        <AnnualRecertMonthPicker
          handleAnnualRecertMonthChange={handleLIHTCAnnualRecertMonthChange}
          editMode={editMode}
          programName={'LIHTC'}
          value={annualRecertMonth}
        />
      </SetupGridItem>
      <Grid item xs={12} sm={6} />
      <SetupGridItem
        label={
          <FormattedMessage
            {...messagesAffordableSetup.autoRolloverIncomeAndAssets}
          />
        }
        TooltipProps={{
          title: (
            <FormattedMessage
              {...messagesAffordableSetup.autoRolloverIncomeAndAssetsDescription}
            />
          ),
        }}
      >
        <SetupSwitch
          switchId={'lihtc-auto-rollover-income-and-assets-switch'}
          editMode={editMode}
          name="setup.LIHTCAutoRolloverIncomeAndAssets"
          disabled={!editMode}
          value={autoRolloverIncomeAndAssets}
          onChange={onChange}
        />
      </SetupGridItem>

      <PropertyStateConfig
        editMode={editMode}
        onChange={onChange}
        state={state}
        stateConfigs={stateConfigs}
        affordableProgramName={'LIHTC'}
      />

      <Grid item xs={12} sm={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messagesAffordableSetup.hotmaActive} />
            <OnOffTypography on={hotmaConfig?.hasAdoptedHOTMA ?? false}>
              {hotmaConfig?.hasAdoptedHOTMA ?? false ? (
                <FormattedMessage {...messagesAffordableSetup.on} />
              ) : (
                <FormattedMessage {...messagesAffordableSetup.off} />
              )}
            </OnOffTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messagesAffordableSetup.hotmaImplementationDate} />
            <SetupValue
              name="LIHTC.HOTMAEffectiveDate"
              value={
                hotmaConfig?.hasAdoptedHOTMA ?? false
                  ? hotmaConfig?.effectiveDate
                  : '---'
              }
              editable={false}
            />
          </Grid>
          <CertificationPastDue
            editMode={editMode}
            value={certificationPastDueDefinition}
            name="setup.LIHTCCertificationPastDueDefinition"
            onChange={onChange}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <ProgramPassbookRate
          key={'LIHTC_PassbookRates'}
          programName={'LIHTC'}
          isLoadingPassbookRateData={isLoadingPassbookRateData}
          passbookRates={passbookRates}
        />
      </Grid>
    </Grid>
  );
};

export default LIHTCSetup;
